import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Calendar,
  DownloadCloud,
  Loader2,
  Briefcase,
  Printer,
  CheckCircle,
  X,
  FileText,
  AlertCircle,
} from "lucide-react";
import secureLocalStorage from "react-secure-storage";

export default function AppliedJobListPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [admitDownloadingId, setAdmitDownloadingId] = useState(null);
  const [acceptingOfferId, setAcceptingOfferId] = useState(null);
  const [error, setError] = useState(null);
  const [selectedLetter, setSelectedLetter] = useState(null);

  const getApi = () =>
    axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

  const getPayload = (extra = {}) => ({
    userId: import.meta.env.VITE_API_USER,
    password: import.meta.env.VITE_API_PASS,
    user: secureLocalStorage.getItem("user"),
    token: secureLocalStorage.getItem("token"),
    ...extra,
  });

  // =========================================================
  // 1. Load Applied Jobs
  // =========================================================
  const jobPost = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getApi().post(
        "/applied-job",
        getPayload()
      );

      const data = response?.data;

      // Supports either a direct array or an API data array.
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : [];

      setJobs(list);
    } catch (err) {
      console.error("Applied Jobs Error:", err);
      setError(
        err.response?.data?.responseMsg ||
        err.message ||
        "Unable to load applied jobs."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    jobPost();
  }, []);

  // =========================================================
  // 2. Download Application Form
  // =========================================================
  async function handleDownload(job) {
	  try {
		setDownloadingId(job.apply_id);
		setError(null);

		const res = await getApi().post(
		  "/application-download",
		  getPayload({ apply_id: job.apply_id })
		);

		if (String(res?.data?.responseCode) !== "200") {
		  throw new Error(
			res?.data?.responseMsg || "Application PDF is unavailable."
		  );
		}

		const pdfPath = res?.data?.pdf_url || res?.data?.url;

		if (!pdfPath) {
		  throw new Error("No PDF path returned by the server.");
		}

		const sourceUrl = import.meta.env.VITE_API_SOURCE_URL;

		const pdfUrl = /^https?:\/\//i.test(pdfPath)
		  ? pdfPath
		  : `${String(sourceUrl).replace(/\/+$/, "")}/${String(
			  pdfPath
			).replace(/^\/+/, "")}`;

		// Open PDF in a new browser tab
		window.open(pdfUrl, "_blank", "noopener,noreferrer");

	  } catch (err) {
		console.error("Application Download Error:", err);

		setError(
		  err.response?.data?.responseMsg ||
		  err.message ||
		  "Unable to open application PDF."
		);
	  } finally {
		setDownloadingId(null);
	  }
	}

  // =========================================================
  // 3. Download / Open Admit Card
  // =========================================================
  async function handleAdmitCard(job) {
    try {
      setAdmitDownloadingId(job.apply_id);
      setError(null);

      const res = await getApi().post(
        "/job-admit-card",
        getPayload({ apply_id: job.apply_id })
      );

      if (String(res?.data?.responseCode) !== "200") {
        throw new Error(
          res?.data?.responseMsg || "Admit Card is not available."
        );
      }

      const pdfPath = res?.data?.responseMsg;

      if (!pdfPath) {
        throw new Error("No Admit Card PDF path returned.");
      }

      const sourceUrl = import.meta.env.VITE_API_SOURCE_URL;
      const pdfUrl = /^https?:\/\//i.test(pdfPath)
        ? pdfPath
        : `${String(sourceUrl).replace(/\/+$/, "")}/${String(
            pdfPath
          ).replace(/^\/+/, "")}`;

      const openedWindow = window.open(
        pdfUrl,
        "_blank",
        "noopener,noreferrer"
      );
      
    } catch (err) {
      console.error("Admit Card Error:", err);
      setError(
        err.response?.data?.responseMsg ||
        err.message ||
        "Failed to open Admit Card."
      );
    } finally {
      setAdmitDownloadingId(null);
    }
  }

  // =========================================================
  // 4. Letter Configuration
  // =========================================================
  const letterTypes = {
    offer_letter: {
      title: "Offer Letter",
      field: "offer_letter",
    },
    appointment_letter: {
      title: "Appointment Letter",
      field: "appointment_letter",
    },
    joining_letter: {
      title: "Joining Letter",
      field: "joining_letter",
    },
  };

  // =========================================================
  // 5. Open Letter Preview
  // =========================================================
  function openLetter(job, type) {
    const config = letterTypes[type];

    if (!config) return;

    const content = job[config.field];

    if (
      typeof content !== "string" ||
      !content.trim()
    ) {
      setError(`${config.title} content is not available.`);
      return;
    }

    setError(null);

    setSelectedLetter({
      apply_id: job.apply_id,
      type,
      title: config.title,
      content,
      accepted: String(job.accept_offer_letter) === "1",
    });
  }

  // =========================================================
  // 6. Print Letter on A4 Paper
  // =========================================================
  function printLetter() {
    if (!selectedLetter?.content) return;

    const printWindow = window.open(
      "",
      "_blank",
      "width=1000,height=800"
    );

    if (!printWindow) {
      setError("Please allow pop-ups to print the letter.");
      return;
    }

    // The letter HTML must be trusted and sanitized server-side.
    const printDocument = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${selectedLetter.title}</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 15mm;
    }

    * {
      box-sizing: border-box;
    }

    html, body {
      margin: 0;
      padding: 0;
      background: #ffffff;
      color: #111827;
      font-family: Arial, sans-serif;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    body {
      font-size: 12pt;
    }

    img {
      max-width: 100%;
    }

    table {
      max-width: 100%;
      border-collapse: collapse;
    }

    pre {
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }

    @media print {
      body {
        margin: 0;
      }

      .no-print {
        display: none !important;
      }
    }
  </style>
</head>
<body>
  ${selectedLetter.content}
  <script>
    window.onload = function () {
      setTimeout(function () {
        window.focus();
        window.print();
      }, 500);
    };
  </script>
</body>
</html>`;

    printWindow.document.open();
    printWindow.document.write(printDocument);
    printWindow.document.close();
  }

  // =========================================================
  // 7. Accept Offer Letter
  // =========================================================
  async function handleAcceptOffer() {
    if (
      !selectedLetter ||
      selectedLetter.type !== "offer_letter" ||
      selectedLetter.accepted
    ) {
      return;
    }

    const applyId = selectedLetter.apply_id;

    const confirmed = window.confirm(
      "Are you sure you want to accept this offer letter?"
    );

    if (!confirmed) return;

    try {
      setAcceptingOfferId(applyId);
      setError(null);

      const res = await getApi().post(
        "/offer-letter-accept",
        getPayload({ apply_id: applyId })
      );

      if (String(res?.data?.responseCode) !== "200") {
        throw new Error(
          res?.data?.responseMsg ||
          "Unable to accept the offer letter."
        );
      }

      // Close the modal and reload the updated application.
      setSelectedLetter(null);
      await jobPost();
    } catch (err) {
      console.error("Offer Acceptance Error:", err);

      setError(
        err.response?.data?.responseMsg ||
        err.message ||
        "Failed to accept the offer letter."
      );
    } finally {
      setAcceptingOfferId(null);
    }
  }

  // =========================================================
  // 8. Render Page
  // =========================================================
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Page Header */}
        <motion.header
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-between mb-6"
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Briefcase className="text-indigo-600" />
            Applied Jobs
          </h1>

          <button
            onClick={jobPost}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-white border border-slate-200
              text-slate-700 font-medium hover:bg-slate-50
              disabled:opacity-60"
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </motion.header>

        {/* Error Message */}
        {error && (
          <div
            role="alert"
            className="mb-5 flex items-start gap-2 rounded-lg border
              border-red-200 bg-red-50 p-4 text-red-700"
          >
            <AlertCircle size={20} className="mt-0.5 shrink-0" />

            <div className="flex-1">{error}</div>

            <button
              onClick={() => setError(null)}
              aria-label="Dismiss error"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-500">
            <Loader2 className="animate-spin mb-3" size={32} />
            <p>Loading applied jobs...</p>
          </div>
        ) : jobs.length === 0 ? (
          <EmptyListSVG />
        ) : (
          <motion.ul layout className="grid gap-4">
            {jobs.map((job) => {
              const isSelected = String(job.selection) === "1";

              const isOfferAccepted =
                String(job.accept_offer_letter) === "1";

              return (
                <motion.li
                  key={job.apply_id}
                  layout
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-xl p-5 shadow-sm border border-slate-100"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
                    {/* Job Information */}
                    <div className="min-w-0">
                      <h2 className="text-lg sm:text-xl font-semibold text-slate-800">
                        {job.designation || "Job Position"}
                      </h2>

                      <p className="text-slate-500 text-sm mt-1">
                        {job.department || "Department not specified"}
                      </p>

                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                        <span>Gender: {job.gender || "-"}</span>
                        <span>Job Type: {job.job_type || "-"}</span>
                        <span>Grade: {job.grade || "-"}</span>
                        {job.position_number && (
                          <span>
                            Position: {job.position_number}
                          </span>
                        )}
                      </div>

                      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          Applied: {formatDate(job.apply_date)}
                        </span>

                        <span>
                          Status:{" "}
                          <span
                            className={`font-semibold ${getStatusColor(
                              job.status
                            )}`}
                          >
                            {job.status || "Unknown"}
                          </span>
                        </span>
                      </div>

                      {/* Selection / Acceptance Status */}
                      {isSelected && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                            <CheckCircle size={14} />
                            Selected
                          </span>

                          {isOfferAccepted && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                              <CheckCircle size={14} />
                              Offer Accepted
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2 items-center lg:justify-end">
                      {/* Application Download */}
                      <button
                        onClick={() => handleDownload(job)}
                        disabled={downloadingId === job.apply_id}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg
                          bg-gradient-to-r from-indigo-600 to-cyan-500
                          text-white text-sm font-medium shadow
                          hover:brightness-105 disabled:opacity-60 transition"
                      >
                        {downloadingId === job.apply_id ? (
                          <Loader2 className="animate-spin" size={16} />
                        ) : (
                          <DownloadCloud size={16} />
                        )}

                        {downloadingId === job.apply_id
                          ? "Preparing..."
                          : "Application"}
                      </button>

                      {/* Admit Card */}
                      <button
                        onClick={() => handleAdmitCard(job)}
                        disabled={admitDownloadingId === job.apply_id}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg
                          bg-gradient-to-r from-emerald-600 to-teal-500
                          text-white text-sm font-medium shadow
                          hover:brightness-105 disabled:opacity-60 transition"
                      >
                        {admitDownloadingId === job.apply_id ? (
                          <Loader2 className="animate-spin" size={16} />
                        ) : (
                          <DownloadCloud size={16} />
                        )}

                        {admitDownloadingId === job.apply_id
                          ? "Preparing..."
                          : "Admit Card"}
                      </button>

                      {/* Offer Letter */}
                      {isSelected && (
                        <button
                          onClick={() =>
                            openLetter(job, "offer_letter")
                          }
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg
                            bg-gradient-to-r from-violet-600 to-indigo-600
                            text-white text-sm font-medium shadow
                            hover:brightness-105 transition"
                        >
                          <FileText size={16} />
                          Offer Letter
                        </button>
                      )}

                      {/* Appointment and Joining Letters */}
                      {isSelected && isOfferAccepted && (
                        <>
                          <button
                            onClick={() =>
                              openLetter(job, "appointment_letter")
                            }
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg
                              bg-gradient-to-r from-blue-600 to-cyan-600
                              text-white text-sm font-medium shadow
                              hover:brightness-105 transition"
                          >
                            <FileText size={16} />
                            Appointment Letter
                          </button>

                          <button
                            onClick={() =>
                              openLetter(job, "joining_letter")
                            }
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg
                              bg-gradient-to-r from-orange-500 to-amber-500
                              text-white text-sm font-medium shadow
                              hover:brightness-105 transition"
                          >
                            <FileText size={16} />
                            Joining Letter
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </motion.li>
              );
            })}
          </motion.ul>
        )}
      </div>

      {/* =====================================================
          LETTER PREVIEW MODAL
      ====================================================== */}
      {selectedLetter && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/70 backdrop-blur-sm p-2 sm:p-5"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedLetter(null);
            }
          }}
        >
          <div className="mx-auto my-2 sm:my-5 w-full max-w-5xl overflow-hidden rounded-xl bg-white shadow-2xl">
            {/* Modal Toolbar */}
            <div className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-3 border-b bg-white p-4">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-slate-800">
                  {selectedLetter.title}
                </h2>

                <p className="text-xs text-slate-500 mt-1">
                  Application ID: {selectedLetter.apply_id}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={printLetter}
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2
                    text-sm font-semibold text-white hover:bg-indigo-700 transition"
                >
                  <Printer size={16} />
                  Print A4
                </button>

                {/* Accept Offer: only for Offer Letter */}
                {selectedLetter.type === "offer_letter" &&
                  !selectedLetter.accepted && (
                    <button
                      onClick={handleAcceptOffer}
                      disabled={
                        acceptingOfferId === selectedLetter.apply_id
                      }
                      className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2
                        text-sm font-semibold text-white hover:bg-emerald-700
                        disabled:cursor-not-allowed disabled:opacity-60 transition"
                    >
                      {acceptingOfferId === selectedLetter.apply_id ? (
                        <Loader2
                          size={16}
                          className="animate-spin"
                        />
                      ) : (
                        <CheckCircle size={16} />
                      )}

                      {acceptingOfferId === selectedLetter.apply_id
                        ? "Accepting..."
                        : "Accept Offer"}
                    </button>
                  )}

                <button
                  onClick={() => setSelectedLetter(null)}
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2
                    text-sm font-semibold text-slate-700 hover:bg-slate-200 transition"
                >
                  <X size={16} />
                  Close
                </button>
              </div>
            </div>

            {/* Accepted Status */}
            {selectedLetter.type === "offer_letter" &&
              selectedLetter.accepted && (
                <div className="border-b border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
                  <CheckCircle
                    size={16}
                    className="mr-2 inline-block"
                  />
                  This offer has already been accepted.
                </div>
              )}

            {/* Letter HTML Content */}
            <div className="overflow-x-auto bg-slate-100 p-3 sm:p-6">
              <div
                className="mx-auto min-h-[297mm] w-full max-w-[210mm] bg-white p-4 shadow-sm sm:p-8"
                style={{
                  color: "#111827",
                  overflowWrap: "break-word",
                }}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: selectedLetter.content,
                  }}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t bg-white p-4">
              <p className="text-xs text-slate-500">
                Use Print A4 to print or save this letter as PDF.
              </p>

              <button
                onClick={() => setSelectedLetter(null)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm
                  font-medium text-slate-700 hover:bg-slate-50"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =========================================================
// Status Color
// =========================================================
function getStatusColor(status) {
  switch (String(status || "").toLowerCase()) {
    case "pending":
      return "text-yellow-600";

    case "approved":
      return "text-green-600";

    case "rejected":
      return "text-red-600";

    default:
      return "text-slate-600";
  }
}

// =========================================================
// Format Date
// =========================================================
function formatDate(value) {
  if (!value) return "-";

  try {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
}

// =========================================================
// Empty Applied Jobs
// =========================================================
function EmptyListSVG() {
  return (
    <div className="rounded-xl border border-slate-100 bg-white p-10 text-center shadow-sm">
      <svg
        width="200"
        height="140"
        viewBox="0 0 200 140"
        className="mx-auto mb-4"
        aria-hidden="true"
      >
        <defs>
          <linearGradient
            id="emptyGradient"
            x1="0"
            y1="0"
            x2="1"
            y2="1"
          >
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#06b6d4" />
          </linearGradient>
        </defs>

        <rect
          x="20"
          y="20"
          width="160"
          height="100"
          rx="12"
          fill="url(#emptyGradient)"
          opacity="0.12"
        />

        <rect
          x="55"
          y="38"
          width="90"
          height="64"
          rx="5"
          fill="white"
          stroke="#6366f1"
          strokeWidth="2"
        />

        <path
          d="M70 55h60 M70 67h60 M70 79h40"
          stroke="#6366f1"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>

      <h3 className="text-lg font-semibold text-slate-800">
        No Applied Jobs Yet
      </h3>

      <p className="mt-2 text-slate-500">
        Once you apply for jobs, they will appear here for tracking
        and downloads.
      </p>
    </div>
  );
}
